import './style.css';
import * as d3 from '../static/js/lib/d3.v3.min.js';
import * as topojson from 'topojson';
import * as THREE from 'three';
import { ARButton } from 'three/examples/jsm/webxr/ARButton.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { getProjection, createPolygonShape } from './geo.js';


/**
 * Environment setup
 * example http://localhost:8080/?env=dev
 */
const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

let environment; // prod, dev, xr

if (urlParams.get('env')) {
    environment = urlParams.get('env')
} else {
    environment = "prod"; // default
}
console.log("Mode: " + environment);


/**
 * Canvas
 */
const canvas = document.querySelector('canvas.webgl')


/**
 * Scene
 */
const scene = new THREE.Scene()


/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}


/**
 * Camera
 */
let camera;
if (environment === "xr") {
    camera = new THREE.PerspectiveCamera(70, sizes.width / sizes.height, 0.01, 20);
} else {
    camera = new THREE.PerspectiveCamera(70, sizes.width / sizes.height, 0.01, 1000);
}
camera.position.z = 10; // Go further away from the scene

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})


/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    alpha: false
});
renderer.physicallyCorrectLights = true;
renderer.outputEncoding = THREE.sRGBEncoding;
renderer.toneMapping = THREE.ReinhardToneMapping;
renderer.toneMappingExposure = 3;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMapSoft = true;
renderer.shadowCameraNear = 1;
renderer.shadowCameraFar = camera.far;
renderer.shadowCameraFov = 60;
renderer.shadowMapBias = 0.0025;
renderer.shadowMapDarkness = 0.5;
renderer.shadowMapWidth = 1024;
renderer.shadowMapHeight = 1024;

if (environment === "xr") {
    renderer.xr.enabled = true;
}


/**
 * Dev Controls
 */
const controls = new OrbitControls(camera, canvas);
controls.rotateSpeed = 1.0;
controls.zoomSpeed = 1.2;
controls.panSpeed = 0.8;
controls.noZoom = false;
controls.noPan = false;
controls.staticMoving = true;
controls.dynamicDampingFactor = 0.3;
controls.keys = [65, 83, 68];
controls.addEventListener('change', render);

/**
 * WebXR Controller
 */
let controller;

if (environment === "xr") {
    /* document.body.appendChild( ARButton.createButton( renderer, {
         optionalFeatures: [ 'dom-overlay', 'dom-overlay-for-handheld-ar' ],
         domOverlay: { root: document.body } } )
     );
     */
    //AR SETUP

    let options = {
        optionalFeatures: ['dom-overlay'],
    }

    options.domOverlay = { root: document.getElementById('content') };

    document.body.appendChild(ARButton.createButton(renderer, options));

    let ar_button = document.getElementById('ARButton');
    ar_button.addEventListener("click", function() {
        openInfoBox();
    }, false);
} else {
    openInfoBox();
}


/**
 * WebXR Hit Test 
 */

let stage = 0;
function onSelect() {
    if (stage >= 1) {
        return;
    } else {
        stage++;

        // To apply more specific position after hit test
        for (let i = 0; i < scene.children.length; i++) {
            scene.children[i].position.set(0, 0, - 0.3).applyMatrix4(controller.matrixWorld);
            scene.children[i].quaternion.setFromRotationMatrix(controller.matrixWorld);
        }

        console.log("Scene Rellocated");

        scene.visible = true; // Show scene
    }
}


/**
 * Lights
 */
var light, spotLight, ambientLight;

light = new THREE.DirectionalLight(0xffffff);
light.position.set(1, 1, 1);
scene.add(light);

spotLight = new THREE.SpotLight(0xffffff);
spotLight.position.set(-1000, -1000, 1000);
spotLight.castShadow = true;
scene.add(spotLight);

ambientLight = new THREE.AmbientLight(0x333333);
scene.add(ambientLight);

if (environment === "dev") {

    const directionalLight = new THREE.DirectionalLight('#ffffff', 20)

    const helper = new THREE.DirectionalLightHelper(directionalLight, 5);
    scene.add(helper);

    scene.add(directionalLight)
}


/**
 * GeoJSON 3D Implementation by Maptime-AMS
 * https://github.com/maptime-ams/geojson-3d
 */
var disableHashChange = false;

// Store downloaded JSON in variable
var json;

// Default settings
var defaults = {
    url: 'data/with_info/greece_regions.geojson', // Default topology loaded
    /*
    color:
    'function(d) {\n' +
    '  return Math.random() * 16777216;\n' +
    '}',
    */
    color:
        'function(d) {\n' +
        'var color = d3.scale.ordinal()\n' +
        '.range([\n' +
        '"#ffffe5",\n' +
        '"#f7fcb9",\n' +
        '"#d9f0a3",\n' +
        '"#addd8e",\n' +
        '"#78c679",\n' +
        '"#41ab5d",\n' +
        '"#238443",\n' +
        '"#006837",\n' +
        '"#004529"\n' +
        '])\n' +
        '.domain(d3.range(0, 13));\n' +
        'return color(d.id);\n' +
        '}',
    height:
        'function(d) {\n' +
        '  return d.area/500\n' +
        '}',
    scale: 0.01
};


var latest = {
    url: null,
    color: null,
    height: null
};


var cmColor = defaults.color;
var cmHeight = defaults.height;


var materials = {
    phong: function (color) {
        return new THREE.MeshPhongMaterial({
            color: color, side: THREE.DoubleSide
            //	phong : new THREE.MeshPhongMaterial({ color: 0xffffff, specular: 0x000000, shininess: 60, shading: THREE.SmoothShading, transparent:true  }),
        });
    },
    meshLambert: function (color) {
        return new THREE.MeshLambertMaterial({
            color: color,
            specular: 0x009900,
            shininess: 30,
            shading: THREE.SmoothShading,
            transparent: true
        });
    },
    meshWireFrame: function (color) {
        return new THREE.MeshBasicMaterial({
            color: color,
            specular: 0x009900,
            shininess: 30,
            shading: THREE.SmoothShading,
            wireframe: true,
            transparent: true
        });
    },
    meshBasic: function (color) {
        return new THREE.MeshBasicMaterial({
            color: color,
            specular: 0x009900,
            shininess: 30,
            shading: THREE.SmoothShading,
            transparent: true
        });
    }
};


/**
 * Raycaster
 */
var raycaster = new THREE.Raycaster();
var raymouse = new THREE.Vector2(1, 1);

function onMouseMoveRay(event) {
    // calculate mouse position in normalized device coordinates
    // (-1 to +1) for both components
    event.preventDefault();
    raymouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    raymouse.y = - (event.clientY / window.innerHeight) * 2 + 1;

}

function onTouchMoveRay(event) {
    // event.preventDefault();
    raymouse.x = (event.touches[0].clientX / window.innerWidth) * 2 - 1;
    raymouse.y = - (event.touches[0].clientY / window.innerHeight) * 2 + 1;
}

document.addEventListener('mousemove', onMouseMoveRay, false);
document.addEventListener('touchmove', onTouchMoveRay, false);


let INTERSECTED;
let INTERSECTEDIDLIST = [];

function raycasterFunction() {

    // Raycaster
    // update the picking ray with the camera and mouse position
    raycaster.setFromCamera(raymouse, camera);

    const intersects = raycaster.intersectObjects(scene.children, true);

    // Material change on hover
    /*
        if (intersects.length > 0) {
            for (let i = 0; i < intersects.length; i++) {
    
                // single intersection color change
                //intersects[i].object.material.color.set(0xff0000);
    
                // parent group intersection color change
                var objectGroup = intersects[i].object.parent;
                for (var j = 0; j < objectGroup.children.length; j++) {
                    objectGroup.children[j].material.color.setHex(0xff0000);
                }
                //console.log(intersects[i].object)
                console.log(objectGroup.name);
    
                if (INTERSECTED != intersects[0].object) {
                    INTERSECTED = intersects[0].object;
                    //if (INTERSECTED) INTERSECTED.visible = false;
                    // trigger once per intersected piece
                    if (INTERSECTED && !INTERSECTEDIDLIST.includes(INTERSECTED.name)) {
                        // While it is animating, keep the name of the intersected into the list and remove them when the animation is complete
                        INTERSECTEDIDLIST.push(INTERSECTED.name);
                    }
                }
            }
    
        } else {
            // if (INTERSECTED) INTERSECTED.visible = true;
            if (INTERSECTED) {
                //INTERSECTED.visible = true;
            }
            INTERSECTED = null;
        }
    */

    // Emissive material on hover
    if (intersects.length > 0) {
        if (INTERSECTED != intersects[0].object) {
            if (INTERSECTED) {
                for (var j = 0; j < INTERSECTED.children.length; j++) {
                    INTERSECTED.children[j].material.emissive.setHex(INTERSECTED.children[j].currentHex);
                }
            }
            INTERSECTED = intersects[0].object.parent;
            for (var j = 0; j < INTERSECTED.children.length; j++) {
                INTERSECTED.children[j].currentHex = INTERSECTED.children[j].material.emissive.getHex();
                INTERSECTED.children[j].material.emissive.setHex(0xff00ff);
            }
            //console.log(INTERSECTED.name);
        }
    } else {
        if (INTERSECTED) {
            for (var j = 0; j < INTERSECTED.children.length; j++) {
                INTERSECTED.children[j].material.emissive.setHex(INTERSECTED.children[j].currentHex);
            }
        }
        INTERSECTED = null;
    }
}


var infoBoxContainer = document.getElementById('infoBoxContainer');

function raycasterFunctionClick() {
    if (INTERSECTED) {
        console.log("Clicked on: " + INTERSECTED.name);
        console.log(INTERSECTED.properties);

        openInfoBox();
        infoBoxContainer.innerHTML =
            '<h1>Περιφέρεια: ' + INTERSECTED.properties.name_greek + '</h1>' +
            '<p>Έδρα: ' + INTERSECTED.properties.seat_greek + '</p>' +
            '<p>Έκτ.(km²): ' + INTERSECTED.properties.area + '</p>' +
            '<p>Πληθυσμός: ' + INTERSECTED.properties.population + '</p>';
    }
}

function onlyClick(ev) {
    // Call preventDefault() to prevent any further handling
    raycasterFunction();
    ev.preventDefault();
    raycasterFunctionClick();
}

document.addEventListener('pointerdown', raycasterFunctionClick);
document.addEventListener('mousemove', raycasterFunction, false);
document.addEventListener("touchmove", raycasterFunction, false);
document.addEventListener("touchstart", raycasterFunction, false);
document.addEventListener("click", onlyClick, false); //https://www.geeksforgeeks.org/how-to-bind-touchstart-and-click-events-but-not-respond-to-both/


/**
 * THREE.JS Animation function
 */
function animate() {
    renderer.setAnimationLoop(render);
}


/**
 * Rendering function
 */
function render() {

    // raycaster can be put here to check each frame
    // or executed via an event listener
    // raycasterFunction();

    // For rendering
    renderer.autoClear = false;
    renderer.clear();
    renderer.render(scene, camera);
}


/**
 * Init stage 1
 */
function init() {
    initTwo();
    animate();
}


/**
 * Init stage 2
 */
function initTwo() {
    render();
    update();

    if (environment === "xr") {
        scene.visible = false; // Hide scene to show it later after relocation
        defaults.scale = 0.00040; // Redcuse scale of the map in AR mode
        controller = renderer.xr.getController(0);
        controller.addEventListener('select', onSelect.bind(this));
        scene.add(controller);
    }
}


function clearGroups() {
    if (json) {
        if (json.type === 'FeatureCollection') {
            json.features.forEach(function (feature) {
                scene.remove(feature._group);
            });
        } else if (json.type === 'Topology') {
            Object.keys(json.objects).forEach(function (key) {
                json.objects[key].geometries.forEach(function (object) {
                    scene.remove(object._group);
                });
            });
        }
    }
    render();
}

function update() {
    clearGroups();

    var width = canvas.clientWidth;
    var height = canvas.clientHeight;

    // Read url from url textarea
    var url = defaults.url;

    d3.json(url, function (data) {

        json = data;

        var functions = {};

        try {
            var colorFunctionString = cmColor;
            functions.color = eval('(' + colorFunctionString + ')');
        } catch (err) {
            console.log(err);
            functions.color = eval('(' + defaults.color + ')');
        }

        try {
            var heightFunctionString = cmHeight;
            functions.height = eval('(' + heightFunctionString + ')');
        } catch (err) {
            console.log(err);
            functions.height = eval('(' + defaults.height + ')');
        }

        if (json.type === 'FeatureCollection') {

            var projection = getProjection(json, width, height);

            json.features.forEach(function (feature) {
                var group = addFeature(feature, projection, functions);
                feature._group = group;
            });

        } else if (json.type === 'Topology') {

            var geojson = topojson.merge(json, json.objects[Object.keys(json.objects)[0]].geometries);
            var projection = getProjection(geojson, width, height);

            Object.keys(json.objects).forEach(function (key) {
                json.objects[key].geometries.forEach(function (object) {
                    var feature = topojson.feature(json, object);
                    var group = addFeature(feature, projection, functions);
                    object._group = group;
                });
            });

        } else {
            console.log('This tutorial only renders TopoJSON and GeoJSON FeatureCollections')
        }

        render();
    });
}

function addShape(group, shape, extrudeSettings, material, color, x, y, z, rx, ry, rz, s) {
    var geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    var mesh = new THREE.Mesh(geometry, materials[material](color));

    // Add shadows
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    mesh.position.set(x, y, z);
    mesh.rotation.set(rx, ry, rz);
    mesh.scale.set(s, s, s);
    group.add(mesh);
}

function addFeature(feature, projection, functions) {
    var group = new THREE.Group();
    group.scale.set(defaults.scale, defaults.scale, defaults.scale);
    group.name = feature.properties.name; // get first column (name) and assign each group name.
    group.properties = feature.properties; // assign geo gson properties of each group to each group.
    //console.log(group.name)

    scene.add(group);

    var color;
    var amount;

    try {
        color = functions.color(feature.properties);
    } catch (err) {
        console.log(err);
    }

    try {
        amount = functions.height(feature.properties);
    } catch (err) {
        console.log(err);
    }

    var extrudeSettings = {
        amount: amount,
        bevelEnabled: false
    };

    var material = 'phong'; // meshLambert, meshWireFrame

    if (feature.geometry.type === 'Polygon') {
        var shape = createPolygonShape(feature.geometry.coordinates, projection);
        addShape(group, shape, extrudeSettings, material, color, 0, 0, amount, Math.PI, 0, 0, 1);
    } else if (feature.geometry.type === 'MultiPolygon') {
        feature.geometry.coordinates.forEach(function (polygon) {
            var shape = createPolygonShape(polygon, projection);
            addShape(group, shape, extrudeSettings, material, color, 0, 0, amount, Math.PI, 0, 0, 1);
        });
    } else {
        console.log('This tutorial only renders Polygons and MultiPolygons')
    }

    return group;
}


function parseHash(hash) {
    params = {};
    hash.split("&").forEach(function (param) {
        if (param.indexOf("=") > -1) {
            var kv = param.split("=");
            params[kv[0]] = decodeURIComponent(kv[1]);
        }
    });

    // TODO: check params before setting
    if (params.url) {
        latest.url = params.url;
        document.getElementById('url').value = params.url;
    }

    if (params.color) {
        latest.color = params.color;
        cmColor = params.color;
    }

    if (params.height) {
        latest.height = params.height;
        cmHeight = params.height;
    }
}

window.onhashchange = function () {
    if (!disableHashChange) {
        parseHash(location.hash.substring(1))
    }
};

if (location.hash) {
    parseHash(location.hash.substring(1));
}

init();